import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Button, TextField, Panel, PageHead, EmptyState, dateTimeET, etSyd } from "@/sentinel";

const when = (ts) => (ts ? `${dateTimeET(ts).slice(0, 10)} ${etSyd(ts)}` : "");

export default function Discussions() {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-messages"], queryFn: api.pmMessages, refetchInterval: 8000 });

  const send = async () => {
    if (!text.trim()) return;
    try { await api.pmPostMessage(text); setText(""); refetch(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-discussions">
      <PageHead crumb={["Project", "Discussions"]} title="Discussions" context="Collaborative chat for the project team." />

      <Panel title="Team thread" icon="MessageSquare" meta={<span className="sn-num">{rows.length}</span>}>
        <div className="sn-stack" style={{ gap: "var(--space-3)" }}>
          <div className="sn-stack" style={{ gap: "var(--space-3)", maxHeight: "calc(100vh - 380px)", minHeight: 240, overflowY: "auto" }} data-testid="pm-messages">
            {rows.length === 0 && <EmptyState icon="MessageSquare" title="No messages yet">Start the conversation below.</EmptyState>}
            {rows.map((m) => {
              const mine = m.author === user?.email;
              return (
                <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
                  <div className="sn-stack" style={{
                    gap: "var(--space-1)", maxWidth: "70%", padding: "var(--space-2) var(--space-3)", borderRadius: "var(--radius-md)",
                    background: mine ? "var(--workspace-accent-dim)" : "var(--surface-sunken)",
                    border: `1px solid ${mine ? "var(--workspace-accent)" : "var(--border-hairline)"}`,
                  }}>
                    <span className="sn-small" style={{ color: mine ? "var(--workspace-accent-fg)" : "var(--text-tertiary)" }}>
                      {m.author} · <span className="sn-num">{when(m.created_at)}</span>
                    </span>
                    <span style={{ whiteSpace: "pre-wrap", color: "var(--text-primary)" }}>{m.body}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="sn-row" style={{ flexWrap: "nowrap" }}>
            <TextField value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Write a message…" aria-label="Message" className="flex-1" data-testid="pm-message-input" />
            <Button variant="primary" icon={Send} onClick={send} data-testid="pm-message-send">Send</Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
